import { useMemo } from 'react'
import clsx from 'clsx'
import { KTIcon } from '../../../../../../_metronic/helpers'
import { buildCoverage, collapseForDisplay } from '../model/routing'
import { computeConfigStatus } from '../model/coverage'
import type { AdapterRow } from '../model/types'
import { formatRub } from '../labels'

interface Props {
  rows: AdapterRow[]
}

interface Issue {
  severity: 'error' | 'warning'
  message: string
}

/**
 * Live configuration-level validation rollup: holes, overlaps, "all disabled",
 * invalid ranges. Inline min>max is also shown per-row in the table; this is the
 * whole-config view. Reuses the same coverage primitives as the simulation.
 */
export const ValidationSummary = ({ rows }: Props) => {
  const issues = useMemo(() => deriveIssues(rows), [rows])
  const status = useMemo(() => computeConfigStatus(rows), [rows])

  if (issues.length === 0) {
    return (
      <div className='notice d-flex bg-light-success rounded border-success border border-dashed p-4'>
        <KTIcon iconName='shield-tick' className='fs-2 text-success me-3' />
        <span className='fw-semibold text-gray-800'>
          Конфигурация валидна — покрытие без дыр, активные адаптеры есть.
        </span>
      </div>
    )
  }

  const tone = status === 'CONFLICT' ? 'danger' : 'warning'

  return (
    <div
      className={clsx(
        'notice d-flex flex-column rounded border border-dashed p-4 gap-2',
        `bg-light-${tone}`,
        `border-${tone}`
      )}
      role='status'
    >
      {issues.map((issue, i) => (
        <span key={i} className='d-flex align-items-start gap-2'>
          <KTIcon
            iconName={issue.severity === 'error' ? 'cross-circle' : 'information-5'}
            className={clsx('fs-3', issue.severity === 'error' ? 'text-danger' : 'text-warning')}
          />
          <span className='fw-semibold text-gray-800'>{issue.message}</span>
        </span>
      ))}
    </div>
  )
}

function deriveIssues(rows: AdapterRow[]): Issue[] {
  const issues: Issue[] = []
  const enabled = rows.filter((r) => r.enabled && r.globallyEnabled)

  const invalid = enabled.filter((r) => r.minDeposit > r.maxDeposit)
  for (const r of invalid) {
    issues.push({
      severity: 'error',
      message: `${r.name}: минимум больше максимума — диапазон не сработает.`,
    })
  }

  if (enabled.length === 0) {
    issues.push({
      severity: 'warning',
      message: 'Ни один адаптер не включён — платежи не маршрутизируются.',
    })
    return issues
  }

  const segments = buildCoverage(rows)
  const collapsed = collapseForDisplay(segments)
  const holes = collapsed.filter((s, i) => s.kind === 'hole' && i > 0 && i < collapsed.length - 1)
  for (const h of holes) {
    issues.push({
      severity: 'warning',
      message: `Дыра в покрытии ${formatRub(h.from)} – ${
        h.to === null ? '∞' : formatRub(h.to)
      }: платежи в этом диапазоне не пройдут.`,
    })
  }

  if (segments.some((s) => s.kind === 'overlap')) {
    issues.push({
      severity: 'warning',
      message:
        'Есть пересечения диапазонов — обработает адаптер с высшим приоритетом (это допустимо).',
    })
  }

  return issues
}
